import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Camera, Lock, Image as ImageIcon, Trash2, MessageSquare, Plus, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { passcode: null, geofence_enabled: false, site_lat: null, site_lng: null, radius_m: 150, site_label: "", photo_enabled: false, messages: [], holiday_pay: { paid: [], hours: 8, waiting_days: 0, full_time_only: false }, holiday_names: [] };

function resizeImage(file, max = 400) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export function KioskSettingsDialog({ open, onOpenChange, company, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [hasPasscode, setHasPasscode] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [picked, setPicked] = useState({});
  const [sugBusy, setSugBusy] = useState(false);
  const loadSuggestions = async () => { setSugBusy(true); try { const { data } = await api.get(`/companies/${company.id}/kiosk-holiday-suggestions`); setSuggestions(data); setPicked(Object.fromEntries(data.map((s) => [s.date, true]))); } catch { toast.error("Could not load holidays"); } finally { setSugBusy(false); } };
  const applySuggestions = () => { const add = suggestions.filter((s) => picked[s.date]).map((s) => ({ text: s.text, start: s.start, end: s.end })); const room = 10 - (form.messages || []).length; if (add.length > room) toast.message(`Only ${room} more message${room === 1 ? "" : "s"} fit — added the soonest ${room}`); setForm({ ...form, messages: [...(form.messages || []), ...add.slice(0, Math.max(0, room))] }); setSuggestions(null); toast.success("Holiday notices added — review and save"); };
  const [logo, setLogo] = useState(null);
  const [color, setColor] = useState("#2563EB");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !company?.id) return;
    setLogo(company.logo_url || null);
    setColor(company.color || "#2563EB");
    api.get(`/companies/${company.id}/kiosk-settings`).then((r) => {
      setForm({ ...EMPTY, ...r.data, passcode: null });
      setHasPasscode(r.data.has_passcode);
    }).catch(() => {});
  }, [open, company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available in this browser");
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, site_lat: +p.coords.latitude.toFixed(6), site_lng: +p.coords.longitude.toFixed(6) })); toast.success("Site coordinates set from your location"); },
      () => toast.error("Could not read your location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    setLogo(await resizeImage(f));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${company.id}/kiosk-settings`, form);
      await api.put(`/companies/${company.id}/branding`, { logo_url: logo ?? "", color });
      toast.success("Settings saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const num = (v) => (v === "" ? null : Number(v));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kiosk-settings-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Kiosk & Branding — {company?.name}</DialogTitle>
          <DialogDescription>Control how this company's time clock and portal look and behave.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="kiosk">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="kiosk" data-testid="tab-kiosk">Time Clock</TabsTrigger>
            <TabsTrigger value="brand" data-testid="tab-brand">Branding</TabsTrigger>
          </TabsList>

          <TabsContent value="kiosk" className="space-y-5 pt-3">
            <section className="space-y-2">
              <div className="flex items-center gap-2 font-medium"><Lock className="w-4 h-4 text-primary" /> Device passcode</div>
              <p className="text-xs text-muted-foreground">Required to exit or switch location on a pinned tablet. {hasPasscode && <span className="text-emerald-600 font-medium">Passcode is set.</span>}</p>
              <div className="flex gap-2">
                <Input data-testid="kiosk-passcode-input" inputMode="numeric" placeholder={hasPasscode ? "Enter new passcode to change" : "4–8 digits"} maxLength={8}
                  value={form.passcode ?? ""} onChange={(e) => setForm({ ...form, passcode: e.target.value.replace(/\D/g, "") })} />
                {hasPasscode && <Button variant="outline" data-testid="kiosk-passcode-remove" onClick={() => setForm({ ...form, passcode: "" })}>Remove</Button>}
              </div>
              {form.passcode === "" && <p className="text-xs text-amber-600">Passcode will be removed on save.</p>}
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-medium"><MapPin className="w-4 h-4 text-primary" /> Location check</div>
                <Switch data-testid="geofence-toggle" checked={form.geofence_enabled} onCheckedChange={(v) => setForm({ ...form, geofence_enabled: v })} />
              </div>
              <p className="text-xs text-muted-foreground">Records each punch's distance from the site. Off-site punches are flagged, never blocked.</p>
              {form.geofence_enabled && (
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="site-label-input" className="col-span-2" placeholder="Site label (e.g. Main Office)" value={form.site_label} onChange={(e) => setForm({ ...form, site_label: e.target.value })} />
                  <Input data-testid="site-lat-input" type="number" step="any" placeholder="Latitude" value={form.site_lat ?? ""} onChange={(e) => setForm({ ...form, site_lat: num(e.target.value) })} />
                  <Input data-testid="site-lng-input" type="number" step="any" placeholder="Longitude" value={form.site_lng ?? ""} onChange={(e) => setForm({ ...form, site_lng: num(e.target.value) })} />
                  <div className="space-y-1">
                    <Label className="text-xs">Radius (meters)</Label>
                    <Input data-testid="site-radius-input" type="number" min={25} value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: Number(e.target.value) })} />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="outline" className="w-full" data-testid="use-my-location-btn" onClick={useMyLocation}><MapPin className="w-4 h-4 mr-1" /> Use my location</Button>
                  </div>
                </div>
              )}
            </section>

            <section className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 font-medium"><Camera className="w-4 h-4 text-primary" /> Photo on punch</div>
                <p className="text-xs text-muted-foreground">Snap a webcam photo on each clock in/out to prevent buddy-punching.</p>
              </div>
              <Switch data-testid="photo-toggle" checked={form.photo_enabled} onCheckedChange={(v) => setForm({ ...form, photo_enabled: v })} />
            </section>

            <section className="space-y-2" data-testid="holiday-pay-section">
              <div>
                <div className="flex items-center gap-2 font-medium"><CalendarDays className="w-4 h-4 text-primary" /> Paid holidays</div>
                <p className="text-xs text-muted-foreground">Tick the federal holidays you pay. Payroll automatically adds a "Holiday pay" line for eligible hourly employees when a paid holiday falls in the pay period.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {(form.holiday_names || []).map((n) => { const on = (form.holiday_pay?.paid || []).includes(n); return (
                  <label key={n} className={`flex items-center gap-2 text-sm rounded-md border px-2.5 py-1.5 cursor-pointer ${on ? "border-primary bg-primary/5" : ""}`}>
                    <input type="checkbox" data-testid={`holiday-paid-${n.replace(/[^a-z]/gi, "")}`} checked={on} onChange={(e) => setForm({ ...form, holiday_pay: { ...form.holiday_pay, paid: e.target.checked ? [...(form.holiday_pay?.paid || []), n] : (form.holiday_pay?.paid || []).filter((x) => x !== n) } })} /> {n}
                  </label>); })}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <label className="flex items-center gap-1.5">Hours per holiday <Input type="number" data-testid="holiday-hours" min={0} max={24} step="0.5" className="h-8 w-20" value={form.holiday_pay?.hours ?? 8} onChange={(e) => setForm({ ...form, holiday_pay: { ...form.holiday_pay, hours: Number(e.target.value) } })} /></label>
                <label className="flex items-center gap-1.5">Eligible after <Input type="number" data-testid="holiday-waiting" min={0} className="h-8 w-20" value={form.holiday_pay?.waiting_days ?? 0} onChange={(e) => setForm({ ...form, holiday_pay: { ...form.holiday_pay, waiting_days: Number(e.target.value) } })} /> days employed</label>
                <label className="flex items-center gap-1.5"><Switch data-testid="holiday-ft-only" checked={!!form.holiday_pay?.full_time_only} onCheckedChange={(v) => setForm({ ...form, holiday_pay: { ...form.holiday_pay, full_time_only: v } })} /> Full-time only</label>
              </div>
            </section>

            <section className="space-y-2">
              <div>
                <div className="flex items-center gap-2 font-medium"><MessageSquare className="w-4 h-4 text-primary" /> Welcome messages</div>
                <p className="text-xs text-muted-foreground">Short notices that rotate under the clock on the kiosk. Leave dates blank to show always, or set a window so holiday notices appear and disappear on their own. Up to 10, 160 characters each.</p>
              </div>
              {(form.messages || []).map((m, i) => {
                const today = new Date().toISOString().slice(0, 10);
                const state = m.end && m.end < today ? ["Expired", "text-muted-foreground"] : m.start && m.start > today ? ["Scheduled", "text-blue-600"] : ["Showing now", "text-emerald-600"];
                const upd = (patch) => setForm({ ...form, messages: form.messages.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
                return (
                  <div key={i} className="rounded-lg border p-2.5 space-y-2" data-testid={`kiosk-message-row-${i}`}>
                    <div className="flex gap-2">
                      <Input data-testid={`kiosk-message-${i}`} maxLength={160} value={m.text} onChange={(e) => upd({ text: e.target.value })} placeholder="e.g. Hard hats required past the yellow line" />
                      <Button type="button" variant="ghost" size="icon" aria-label="Remove message" data-testid={`kiosk-message-remove-${i}`} onClick={() => setForm({ ...form, messages: form.messages.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-muted-foreground">Show from</span><Input type="date" data-testid={`kiosk-message-start-${i}`} className="h-8 w-[150px]" value={m.start || ""} onChange={(e) => upd({ start: e.target.value || null })} />
                      <span className="text-muted-foreground">until</span><Input type="date" data-testid={`kiosk-message-end-${i}`} className="h-8 w-[150px]" value={m.end || ""} onChange={(e) => upd({ end: e.target.value || null })} />
                      <span className={`ml-auto font-medium ${state[1]}`} data-testid={`kiosk-message-state-${i}`}>{state[0]}</span>
                    </div>
                  </div>
                );
              })}
              {(form.messages || []).length < 10 && <div className="flex flex-wrap gap-2"><Button type="button" variant="outline" size="sm" data-testid="kiosk-message-add" onClick={() => setForm({ ...form, messages: [...(form.messages || []), { text: "", start: null, end: null }] })}><Plus className="w-4 h-4 mr-1" /> Add message</Button>
                <Button type="button" variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="kiosk-holiday-suggest" disabled={sugBusy} onClick={loadSuggestions}><CalendarDays className="w-4 h-4 mr-1" /> Suggest holiday notices</Button></div>}
              {suggestions && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="kiosk-holiday-suggestions">
                  <div className="flex items-center justify-between gap-2"><div className="text-sm font-medium text-emerald-900">Upcoming federal holidays — tick the ones to add</div><button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => setSuggestions(null)}>Close</button></div>
                  {suggestions.length === 0 && <p className="text-xs text-muted-foreground">All upcoming holidays already have notices.</p>}
                  {suggestions.map((s, i) => (
                    <label key={s.date} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`kiosk-holiday-${i}`}>
                      <input type="checkbox" className="mt-1" checked={!!picked[s.date]} onChange={(e) => setPicked({ ...picked, [s.date]: e.target.checked })} data-testid={`kiosk-holiday-check-${i}`} />
                      <span><span className="font-medium">{s.holiday}</span> <span className="text-xs text-muted-foreground">· shows {s.start} → {s.date}</span><div className="text-xs text-muted-foreground">{s.text}</div></span>
                    </label>
                  ))}
                  {suggestions.length > 0 && <div className="flex gap-2 pt-1"><Button type="button" size="sm" data-testid="kiosk-holiday-apply" disabled={!Object.values(picked).some(Boolean)} onClick={applySuggestions}>Add {Object.values(picked).filter(Boolean).length || ""} to messages</Button><Button type="button" size="sm" variant="ghost" onClick={() => setPicked(Object.fromEntries(suggestions.map((s) => [s.date, true])))}>Select all</Button></div>}
                </div>
              )}
            </section>
          </TabsContent>

          <TabsContent value="brand" className="space-y-4 pt-3">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl border border-border bg-white grid place-items-center overflow-hidden">
                {logo ? <img data-testid="brand-logo-preview" src={logo} alt="logo" className="w-full h-full object-contain p-1" /> : <span className="text-4xl">{company?.logo}</span>}
              </div>
              <div className="space-y-2 flex-1">
                <Label className="flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> Company logo</Label>
                <Input data-testid="brand-logo-file" type="file" accept="image/*" onChange={onLogoFile} />
                <p className="text-xs text-muted-foreground">PNG/SVG/JPG. Shown in the portal header, time clock and printed kiosk kit.</p>
                {logo && <Button size="sm" variant="ghost" data-testid="brand-logo-remove" onClick={() => setLogo(null)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove logo</Button>}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Brand color</Label>
              <div className="flex items-center gap-2">
                <input data-testid="brand-color-input" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-12 rounded border border-border bg-transparent" />
                <Input value={color} onChange={(e) => setColor(e.target.value)} className="w-32 font-mono-num" />
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button data-testid="kiosk-settings-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
